<script setup lang="ts">
import { TenantLicenseDataDto } from '@/api/generator';
import EntityLink from '@/components/EntityLink/EntityLink.vue';
import DateTimeFormatter from '@/components/formatters/DateTimeFormatter.vue';
import FormLabel from '@/components/FormLabel/FormLabel.vue';
import TextField from '@/components/forms/TextField/TextField.vue';
import { EMPTY_FIELD_SIGN } from '@/components/forms/utils.ts';
import { error, warningSign } from '@/components/icons/icons.ts';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import OverviewTable from '@/components/OverviewTable/OverviewTable.vue';
import { OverviewTableProps } from '@/components/OverviewTable/types.ts';
import { useLicenseStore } from '@/stores/licenseStore.ts';
import { I18nKey } from '@/utils/typings';
import { useTranslation } from 'i18next-vue';
import { storeToRefs } from 'pinia';
import { useFormValues } from 'vee-validate';
import { computed, onBeforeMount } from 'vue';

interface LicenseFormProps {
  hasLicense?: boolean;
}

defineProps<LicenseFormProps>();

const { t } = useTranslation();
const values = useFormValues<TenantLicenseDataDto>();

const siteName = computed(
  () => values.value?.licensePlan?.customerProvidedOptionalSiteName || null,
);

const licencedDevicesHeaders = computed<
  OverviewTableProps<TenantLicenseDataDto, string>['headers']
>(() => [
  {
    title: `${t('Model')} | ${t('SubModel')}`,
    key: 'productName',
  },
  {
    title: `${t('Name')} | ${t('ID')}`,
    key: 'name',
  },
  {
    title: t('SerialNumber'),
    key: 'serialNumber',
  },
  {
    title: t('DeliveryDate'),
    key: 'deliveryDate',
  },
  {
    title: t('ExpirationDate'),
    key: 'expirationDate',
  },
]);

const licenseStore = useLicenseStore();
const {
  licenseIsAboutToExpire,
  daysToLicenseExpiration,
  deviceLicensesAboutToExpire,
  expiredDeviceLicenses,
  licenseHasExpired,
} = storeToRefs(licenseStore);

const licenseExpirationAlertTitle = computed(() =>
  licenseHasExpired.value ? t('LicenseHasExpired') : t('YourLicenseIsAboutToExpireSoon'),
);
const licenseHasExpiredMessage = computed(() =>
  licenseHasExpired.value
    ? t('LicenseHasExpiredMsg')
    : t('YourLicenseWillExpireIn_count_DaysMsg', {
        count: daysToLicenseExpiration.value,
      }),
);

onBeforeMount(async () => {
  await licenseStore.fetchLicenseData();
});
</script>

<template>
  <h4>
    {{ $t('Customer') }}
  </h4>
  <v-row>
    <v-col cols="4">
      <TextField
        :label="$t('InstallationName')"
        :modelValue="siteName ?? EMPTY_FIELD_SIGN"
        mode="details"
      />
    </v-col>
    <v-col>
      <TextField
        :label="$t('InstallationPIN')"
        :modelValue="values?.licensePlan?.ipin ?? EMPTY_FIELD_SIGN"
        mode="details"
      />
    </v-col>
    <v-col>
      <TextField
        :label="$t('CompanyID')"
        :modelValue="values?.licensePlan?.companyId ?? EMPTY_FIELD_SIGN"
        mode="details"
      />
    </v-col>
  </v-row>
  <VDivider color="primary-lighten-3" class="my-4" />
  <h4>
    {{ $t('LicensePlan') }}
  </h4>
  <v-alert
    v-if="licenseIsAboutToExpire"
    :type="licenseHasExpired ? 'error' : 'warning'"
    :title="licenseExpirationAlertTitle"
    :icon="licenseHasExpired ? error : warningSign"
    class="mb-6 text-left"
  >
    <p class="mb-2">
      {{ licenseHasExpiredMessage }}
    </p>
    <p class="mb-2">{{ $t('LicenseHasExpiredRemedy') }}</p>
  </v-alert>
  <v-row>
    <v-col cols="4">
      <TextField
        :label="$t('LicenseBundle')"
        :modelValue="$t(`LicenseBundleLabelEnum.${values?.licensePlan?.bundle}` as I18nKey)"
        mode="details"
      />
    </v-col>
    <v-col>
      <FormLabel :label="$t('DeliveryDate')" />
      <DateTimeFormatter :value="values?.licensePlan?.deliveryDate" />
    </v-col>
    <v-col>
      <FormLabel :label="$t('ExpirationDate')" />
      <DateTimeFormatter
        :class="{
          'text-warning font-weight-semibold': licenseIsAboutToExpire && !licenseHasExpired,
          'text-error font-weight-semibold': licenseHasExpired,
        }"
        :value="values?.licensePlan?.expirationDate"
      />
    </v-col>
  </v-row>
  <template v-if="values?.devices?.length">
    <VDivider color="primary-lighten-3" class="my-4" />
    <h4>
      {{ $t('LicenseDevices') }}
    </h4>

    <OverviewTable
      class="w-100 mt-2"
      :headers="licencedDevicesHeaders"
      :items="values.devices"
      :zebra="true"
      :noSpacing="true"
      elevation="0"
      data-testid="DataTable_LicensedDevices"
    >
      <template #item.productName="{ item }">
        <div>
          <IdentifierLabel :label="item.model" :identifier="item.subModel" />
          <v-alert
            v-if="
              deviceLicensesAboutToExpire.has(item.masterDeviceId) ||
              expiredDeviceLicenses.has(item.masterDeviceId)
            "
            :type="expiredDeviceLicenses.has(item.masterDeviceId) ? 'error' : 'warning'"
            :icon="expiredDeviceLicenses.has(item.masterDeviceId) ? error : warningSign"
            density="compact"
            class="mb-2"
          >
            <template v-if="expiredDeviceLicenses.has(item.masterDeviceId)">
              <p class="mb-2">{{ $t('TheDevicesLicenseHasExpired') }}</p>
              <p class="mb-2">
                {{ $t('LicenseHasExpiredRemedy') }}
              </p>
            </template>
            <template v-else>
              <p class="mb-2">
                {{
                  $t('TheDevicesLicenseWillExpireIn_count_Days', {
                    count: deviceLicensesAboutToExpire.get(item.masterDeviceId)?.daysToExpiration,
                  })
                }}
                {{ $t('DeviceWillBeDisabledMsg') }}
              </p>
              <p class="mb-2">
                {{ $t('LicenseHasExpiredRemedy') }}
              </p>
            </template>
          </v-alert>
        </div>
      </template>

      <template #item.name="{ item }">
        <EntityLink
          v-if="item.id && item.name && item.iuid"
          :id="item.id"
          :name="item.name"
          :iuid="item.iuid"
          type="qaEquipment"
        />
        <template v-else>
          {{ EMPTY_FIELD_SIGN }}
        </template>
      </template>

      <template #item.serialNumber="{ item }">
        {{ item.serialNumber || EMPTY_FIELD_SIGN }}
      </template>

      <template #item.deliveryDate="{ item }">
        <DateTimeFormatter :value="item.deliveryDate" />
      </template>

      <template #item.expirationDate="{ item }">
        <DateTimeFormatter
          :value="item.expirationDate"
          :class="{
            'text-warning font-weight-semibold': deviceLicensesAboutToExpire.has(
              item.masterDeviceId,
            ),
            'text-error font-weight-bold': expiredDeviceLicenses.has(item.masterDeviceId),
          }"
        />
      </template>
    </OverviewTable>
  </template>
</template>
